// GENERATED by scripts/gen-themes.mjs — do not edit by hand.
// Run `pnpm gen:themes` after editing PALETTES in that script.
//
// Kept in sync with src/themes.css and the boot script in index.html by
// construction: all three come from one generator run, so a palette can never
// appear in the picker without its tokens existing (or vice versa).

/** A palette's real rendered colours for one mode, as hex. Derived from the
 *  same token values the CSS uses, so a preview swatch always shows the theme
 *  you actually get. */
export interface PalettePreview {
  /** Page background. Also what the PWA theme-color meta is set to. */
  bg: string;
  /** Cards and panels. */
  surface: string;
  /** Muted fills — the row-hover / chip background. */
  fill: string;
  /** Body text. */
  text: string;
  /** Secondary text. */
  muted: string;
  border: string;
  /** Buttons, active nav, focus. The token that actually distinguishes one
   *  palette from another — see the note at the top of the generator. */
  accent: string;
}

export type PaletteFamily =
  | 'Neutral'
  | 'Cool'
  | 'Green'
  | 'Warm'
  | 'Purple';

export interface PaletteMeta {
  key: PaletteKey;
  label: string;
  /** Grouping shown as a heading in the picker. */
  family: PaletteFamily;
  /** One line shown under the name in the picker. */
  blurb: string;
  /** Body-text contrast ratio per mode, e.g. 14.1. Surfaced in the picker so
   *  the accessibility claim is visible rather than asserted. */
  contrast: { light: number; dark: number };
  preview: { light: PalettePreview; dark: PalettePreview };
}

export type PaletteKey =
  | 'default'
  | 'graphite'
  | 'contrast'
  | 'slate'
  | 'ocean'
  | 'sky'
  | 'steel'
  | 'nord'
  | 'forest'
  | 'mint'
  | 'moss'
  | 'sepia'
  | 'sandstone'
  | 'espresso'
  | 'gruvbox'
  | 'solarized'
  | 'lavender'
  | 'plum'
  | 'dracula'
  | 'rose'
  | 'crimson'
  | 'midnight';

export const PALETTE_FAMILIES: PaletteFamily[] = [
  'Neutral',
  'Cool',
  'Green',
  'Warm',
  'Purple',
];

export const PALETTES: PaletteMeta[] = [
  {
    key: 'default',
    label: 'Neutral',
    family: 'Neutral',
    blurb: 'Crisp greys, blue accent. The original look.',
    contrast: { light: 16.1, dark: 18.8 },
    preview: {
      light: { bg: '#ffffff', surface: '#ffffff', fill: '#e8e8e8', text: '#212121', muted: '#606060', border: '#e0e0e0', accent: '#2866eb' },
      dark: { bg: '#121212', surface: '#171717', fill: '#323232', text: '#ffffff', muted: '#acacac', border: '#2e2e2e', accent: '#4d7ee7' },
    },
  },
  {
    key: 'graphite',
    label: 'Graphite',
    family: 'Neutral',
    blurb: 'Warm charcoal greys with a soft amber accent.',
    contrast: { light: 14.5, dark: 17.6 },
    preview: {
      light: { bg: '#f5f4f2', surface: '#fbfaf9', fill: '#ece8e4', text: '#24211e', muted: '#645e59', border: '#dad6d2', accent: '#7b6e60' },
      dark: { bg: '#1b1815', surface: '#23201d', fill: '#36322d', text: '#ffffff', muted: '#b1adaa', border: '#383431', accent: '#908274' },
    },
  },
  {
    key: 'contrast',
    label: 'High contrast',
    family: 'Neutral',
    blurb: 'Pure black on white, heavy borders. For bright rooms or low vision.',
    contrast: { light: 21.0, dark: 21.0 },
    preview: {
      light: { bg: '#ffffff', surface: '#ffffff', fill: '#ebebeb', text: '#000000', muted: '#424242', border: '#737373', accent: '#000000' },
      dark: { bg: '#000000', surface: '#0d0d0d', fill: '#242424', text: '#ffffff', muted: '#d1d1d1', border: '#949494', accent: '#ffffff' },
    },
  },
  {
    key: 'slate',
    label: 'Slate',
    family: 'Cool',
    blurb: 'Cool blue-grey with an indigo accent. Calm for long shifts.',
    contrast: { light: 15.1, dark: 18.0 },
    preview: {
      light: { bg: '#eef3f9', surface: '#f7f9fd', fill: '#dbe6f5', text: '#161d27', muted: '#4e5d71', border: '#ccd6e3', accent: '#5a53ea' },
      dark: { bg: '#101721', surface: '#161e2a', fill: '#242f3f', text: '#ffffff', muted: '#9fa9b6', border: '#2a333e', accent: '#7973e8' },
    },
  },
  {
    key: 'ocean',
    label: 'Ocean',
    family: 'Cool',
    blurb: 'Deep sea blues with a bright cyan accent.',
    contrast: { light: 14.5, dark: 17.5 },
    preview: {
      light: { bg: '#eaf6fd', surface: '#f5fbfe', fill: '#d3edfd', text: '#17232b', muted: '#4a6373', border: '#c4dbe9', accent: '#097c89' },
      dark: { bg: '#0b1b26', surface: '#102430', fill: '#1c3747', text: '#ffffff', muted: '#9fb2bf', border: '#243845', accent: '#12a0af' },
    },
  },
  {
    key: 'sky',
    label: 'Sky',
    family: 'Cool',
    blurb: 'Airy pale blue. Bright without the glare of white.',
    contrast: { light: 14.7, dark: 17.7 },
    preview: {
      light: { bg: '#e9f5fe', surface: '#f5fafe', fill: '#d3eafd', text: '#162129', muted: '#4a6073', border: '#c1daee', accent: '#0c66ed' },
      dark: { bg: '#081a29', surface: '#0c2234', fill: '#17344d', text: '#ffffff', muted: '#9dafbe', border: '#203649', accent: '#3981ec' },
    },
  },
  {
    key: 'steel',
    label: 'Steel',
    family: 'Cool',
    blurb: 'Muted blue-steel with a restrained teal accent.',
    contrast: { light: 14.8, dark: 17.9 },
    preview: {
      light: { bg: '#eff3f8', surface: '#f8fafc', fill: '#dee7f2', text: '#1a1f26', muted: '#535f6c', border: '#cfd7e0', accent: '#25788b' },
      dark: { bg: '#12181e', surface: '#191f27', fill: '#28313c', text: '#ffffff', muted: '#a3aab3', border: '#2d343c', accent: '#3393a8' },
    },
  },
  {
    key: 'nord',
    label: 'Nord',
    family: 'Cool',
    blurb: 'The arctic classic. Frost blue on polar night.',
    contrast: { light: 15.8, dark: 16.4 },
    preview: {
      light: { bg: '#f5f7f9', surface: '#ffffff', fill: '#dde4f3', text: '#171c26', muted: '#525c70', border: '#d4dae5', accent: '#3b7691' },
      dark: { bg: '#1c1f26', surface: '#242932', fill: '#313744', text: '#ffffff', muted: '#abb1bd', border: '#353c4b', accent: '#4c8dab' },
    },
  },
  {
    key: 'forest',
    label: 'Forest',
    family: 'Green',
    blurb: 'Deep pine greens with an emerald accent.',
    contrast: { light: 13.7, dark: 16.7 },
    preview: {
      light: { bg: '#edfaf4', surface: '#f7fdfa', fill: '#daf6e9', text: '#192c23', muted: '#4a6d5d', border: '#c5e0d3', accent: '#157f58' },
      dark: { bg: '#0f2119', surface: '#152b21', fill: '#234033', text: '#ffffff', muted: '#aac0b6', border: '#283e34', accent: '#21a172' },
    },
  },
  {
    key: 'mint',
    label: 'Mint',
    family: 'Green',
    blurb: 'Cool fresh green-cyan. Light and clean.',
    contrast: { light: 13.2, dark: 16.3 },
    preview: {
      light: { bg: '#ebfcf9', surface: '#f6fefd', fill: '#d6faf4', text: '#1a302c', muted: '#487069', border: '#bde3dc', accent: '#117e73' },
      dark: { bg: '#0d2420', surface: '#122e29', fill: '#1f453e', text: '#ffffff', muted: '#adc7c3', border: '#25413c', accent: '#1da597' },
    },
  },
  {
    key: 'moss',
    label: 'Moss',
    family: 'Green',
    blurb: 'Earthy olive with a warm lime accent.',
    contrast: { light: 13.4, dark: 16.7 },
    preview: {
      light: { bg: '#f4f9ee', surface: '#fafdf7', fill: '#e9f4dc', text: '#242d1b', muted: '#5d6b4c', border: '#d3dec7', accent: '#587a1d' },
      dark: { bg: '#192010', surface: '#202917', fill: '#333e25', text: '#ffffff', muted: '#b6beac', border: '#343c2a', accent: '#70982a' },
    },
  },
  {
    key: 'sepia',
    label: 'Sepia',
    family: 'Warm',
    blurb: 'Warm paper tones. Easiest on the eyes for heavy reading.',
    contrast: { light: 14.0, dark: 17.1 },
    preview: {
      light: { bg: '#fbf5ed', surface: '#fdfbf6', fill: '#f8ecd8', text: '#2d2518', muted: '#716249', border: '#e2d8c5', accent: '#9a641d' },
      dark: { bg: '#221b0e', surface: '#2c2314', fill: '#423622', text: '#ffffff', muted: '#bfb6a6', border: '#3f3627', accent: '#b67a2a' },
    },
  },
  {
    key: 'sandstone',
    label: 'Sandstone',
    family: 'Warm',
    blurb: 'Desert tan with a terracotta accent.',
    contrast: { light: 14.2, dark: 17.4 },
    preview: {
      light: { bg: '#fcf3eb', surface: '#fefaf5', fill: '#fce7d4', text: '#2d2218', muted: '#735d4a', border: '#e7d4c3', accent: '#c3441d' },
      dark: { bg: '#25170b', surface: '#2f1f10', fill: '#46301d', text: '#ffffff', muted: '#c0afa0', border: '#443325', accent: '#d95d38' },
    },
  },
  {
    key: 'espresso',
    label: 'Espresso',
    family: 'Warm',
    blurb: 'Dark roast browns with a caramel accent.',
    contrast: { light: 14.8, dark: 17.8 },
    preview: {
      light: { bg: '#f9f2ee', surface: '#fcf9f7', fill: '#f3e4dd', text: '#271d18', muted: '#6f5b51', border: '#e1d4ce', accent: '#8a6c11' },
      dark: { bg: '#1f1611', surface: '#291d17', fill: '#3d2e26', text: '#ffffff', muted: '#b6a9a2', border: '#3d312b', accent: '#a6841b' },
    },
  },
  {
    key: 'gruvbox',
    label: 'Gruvbox',
    family: 'Warm',
    blurb: 'Warm retro contrast with a burnt-orange accent.',
    contrast: { light: 12.9, dark: 16.1 },
    preview: {
      light: { bg: '#f7f2de', surface: '#fcf9ee', fill: '#f8efd8', text: '#2e2919', muted: '#706548', border: '#ded5bc', accent: '#ba4e0f' },
      dark: { bg: '#212121', surface: '#2b2826', fill: '#423922', text: '#ffffff', muted: '#c1bba9', border: '#423b29', accent: '#da611b' },
    },
  },
  {
    key: 'solarized',
    label: 'Solarized',
    family: 'Warm',
    blurb: 'The classic low-fatigue scheme. Cream and teal.',
    contrast: { light: 14.8, dark: 15.4 },
    preview: {
      light: { bg: '#fdf6e2', surface: '#fefaf1', fill: '#ede7d4', text: '#152428', muted: '#47646b', border: '#c7dde3', accent: '#2075b1' },
      dark: { bg: '#002933', surface: '#06313c', fill: '#123e49', text: '#ffffff', muted: '#aabcc0', border: '#2e4248', accent: '#2e8acc' },
    },
  },
  {
    key: 'lavender',
    label: 'Lavender',
    family: 'Purple',
    blurb: 'Soft violet-grey with a bright violet accent.',
    contrast: { light: 16.3, dark: 17.2 },
    preview: {
      light: { bg: '#f2ecfb', surface: '#f9f6fe', fill: '#e4d7f9', text: '#140f1c', muted: '#5e4e79', border: '#d8cee8', accent: '#7d4ced' },
      dark: { bg: '#150e23', surface: '#1c132d', fill: '#2d2143', text: '#f6f4f8', muted: '#a296b5', border: '#332944', accent: '#9069ea' },
    },
  },
  {
    key: 'plum',
    label: 'Plum',
    family: 'Purple',
    blurb: 'Deep magenta-grey with a fuchsia accent.',
    contrast: { light: 15.3, dark: 18.2 },
    preview: {
      light: { bg: '#f8eef9', surface: '#fcf7fd', fill: '#f3dbf5', text: '#241525', muted: '#6f4f72', border: '#e2cee4', accent: '#b922d0' },
      dark: { bg: '#1f1021', surface: '#28162a', fill: '#3d243f', text: '#ffffff', muted: '#b39eb5', border: '#3e2b40', accent: '#c84ddb' },
    },
  },
  {
    key: 'dracula',
    label: 'Dracula',
    family: 'Purple',
    blurb: 'The cult dark scheme. Deep indigo and pink.',
    contrast: { light: 15.6, dark: 16.2 },
    preview: {
      light: { bg: '#eef0f9', surface: '#f7f8fc', fill: '#dde0f3', text: '#151723', muted: '#535771', border: '#d1d4e3', accent: '#d11780' },
      dark: { bg: '#1e2029', surface: '#272934', fill: '#373949', text: '#ffffff', muted: '#b2b5c2', border: '#383c4f', accent: '#e2419c' },
    },
  },
  {
    key: 'rose',
    label: 'Rose',
    family: 'Purple',
    blurb: 'Soft mauve neutrals with a rose accent.',
    contrast: { light: 15.7, dark: 18.2 },
    preview: {
      light: { bg: '#fbedf0', surface: '#fdf6f8', fill: '#f8d8e0', text: '#231317', muted: '#764c56', border: '#e7cdd4', accent: '#d61c5a' },
      dark: { bg: '#220e13', surface: '#2c141a', fill: '#42222a', text: '#fefdfe', muted: '#b79ca3', border: '#42292f', accent: '#e04a7c' },
    },
  },
  {
    key: 'crimson',
    label: 'Crimson',
    family: 'Purple',
    blurb: 'Warm red-grey with a deep crimson accent.',
    contrast: { light: 15.5, dark: 18.4 },
    preview: {
      light: { bg: '#f9eeee', surface: '#fdf7f7', fill: '#f5dbdc', text: '#241415', muted: '#724f51', border: '#e5cfd0', accent: '#d32e22' },
      dark: { bg: '#211010', surface: '#2a1617', fill: '#3f2425', text: '#ffffff', muted: '#b49d9d', border: '#402b2c', accent: '#db584f' },
    },
  },
  {
    key: 'midnight',
    label: 'Midnight',
    family: 'Neutral',
    blurb: 'True black for OLED screens. Electric blue accent.',
    contrast: { light: 14.9, dark: 18.1 },
    preview: {
      light: { bg: '#f0f2f7', surface: '#f8f9fc', fill: '#e0e5f0', text: '#1b1e25', muted: '#555c6a', border: '#d1d5de', accent: '#2961fa' },
      dark: { bg: '#000000', surface: '#0b0c0f', fill: '#1d2026', text: '#edeef0', muted: '#9298a2', border: '#24272e', accent: '#4e7af3' },
    },
  },
];

export const DEFAULT_PALETTE: PaletteKey = 'default';

/** Narrow an unknown (localStorage, a URL param, a stale value from a previous
 *  release) to a palette we actually ship. Anything else falls back to Neutral
 *  rather than stamping a data-palette no stylesheet matches. */
export function isPaletteKey(v: unknown): v is PaletteKey {
  return typeof v === 'string' && PALETTES.some((p) => p.key === v);
}

export function getPalette(key: PaletteKey): PaletteMeta {
  return PALETTES.find((p) => p.key === key) ?? PALETTES[0];
}

/** Picker grouping. Preserves PALETTES order within each family. */
export function palettesByFamily(): Array<{ family: PaletteFamily; items: PaletteMeta[] }> {
  return PALETTE_FAMILIES.map((family) => ({
    family,
    items: PALETTES.filter((p) => p.family === family),
  })).filter((g) => g.items.length > 0);
}
